import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import axiosInstance from '../tiptap/lib/axios';
import { toast } from 'sonner';
import { Button } from '../tiptap/components/ui/button';
import { ArrowLeft, Settings2, Sparkles, Shield, FileText, Download, Loader2, RectangleHorizontal, RectangleVertical, StickyNote, LogOut, ChevronUp, ChevronDown, Sun, MoreHorizontal, ArrowUpDown, GitCompare, Workflow, BookMarked } from 'lucide-react';
import HelpPanel from '../components/HelpPanel';
import ContinuousDocument from '../tiptap/components/ContinuousDocument';
import SpreadsheetSheet from '../tiptap/components/Spreadsheet/SpreadsheetSheet';
import ChatSidebar from '../tiptap/components/ChatSidebar';
import PropertiesPanel from '../tiptap/components/properties-panel';
import ConfigPanel from '../tiptap/components/ConfigPanel';
import CommandManagement from '../tiptap/components/CommandManagement';
import FlowsModal from '../tiptap/components/FlowsModal';
import ReferencesHost from '../tiptap/components/References';
import QuickCommandModal from '../tiptap/components/QuickCommandModal';
import DocumentPromptPanel from '../tiptap/components/DocumentPromptPanel';
import SheetDiffView from '../tiptap/components/SheetDiffView';
import FloatingAIButton from '../tiptap/components/FloatingAIButton';
import FloatingImagePropsButton from '../tiptap/components/FloatingImagePropsButton';
import FloatingCommentButton from '../tiptap/components/FloatingCommentButton';
import CreditBadge from '../tiptap/components/CreditsPanel';
import ImageLightboxHost from '../tiptap/components/ImageLightbox/ImageLightboxHost';
import CommentsMarginColumn from '../tiptap/components/CommentsMarginColumn';
import { OPEN_COMMENT_THREAD_EVENT } from '../tiptap/extensions/CommentExtension';
import { notifyCreditsCharged } from '../tiptap/lib/credits';
import SheetTabs from '../tiptap/components/SheetTabs';
import SheetReorderDialog from '../tiptap/components/SheetReorderDialog';
import WordCountBar from '../tiptap/components/WordCountBar';
import TableOptionsSidebar from '../tiptap/components/TableOptionsSidebar';
import ThemeToggle from '../components/ThemeToggle';
import ConfirmDialog from '../components/ConfirmDialog';
import { findReplace } from '../tiptap/lib/findReplace';
import { safeNodeAt } from '../tiptap/lib/safeNodeAt';
import useEditorLock from '../tiptap/hooks/useEditorLock';
import useIsDesktop from '../hooks/useIsDesktop';
import { buildStepPrompt } from '../tiptap/lib/macroPrompt';
import { NodeSelection } from '@tiptap/pm/state';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../tiptap/components/ui/dialog';
import {
  DropdownMenu as UIDropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '../tiptap/components/ui/dropdown-menu';
import { useConfigStore } from '../store/configStore';
import { useCommandsStore } from '../tiptap/store/commandsStore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { renderToStaticMarkup } from 'react-dom/server';

const EditorPage = () => {
  const { documentId } = useParams();
  const { token, user, logout } = useAuthStore();
  const { apiKey, model, imageModel, describeModel, systemPrompt } = useConfigStore();
  const loadServerDefaults = useCommandsStore((s) => s.loadServerDefaults);
  const navigate = useNavigate();
  const location = useLocation();
  // T-120b: tema via store (misma ruta que ThemeToggle.js). Ya no hay dos
  // configStore: el de tiptap quedó como alias del canónico.
  const toggleTheme = useConfigStore((s) => s.toggleTheme);

  // T-037: merge admin system commands into the store once, on editor entry;
  // covers both consumers (CommandManagement + QuickCommandModal).
  useEffect(() => {
    loadServerDefaults();
  }, [loadServerDefaults]);

  const [document, setDocument] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeSheetId, setActiveSheetId] = useState(null);
  const [loading, setLoading] = useState(true);
  // T-120a: breakpoint reactivo compartido via useIsDesktop (extraccion del
  // inline que vivia aqui desde T-118). El hook escucha matchMedia con listener.
  const isDesktop = useIsDesktop();
  const [chatOpen, setChatOpen] = useState(() => {
    try { return window.matchMedia('(min-width: 1024px)').matches; } catch { return true; }
  });
  // T-117: zoom del documento movido desde ContinuousDocument. El estado y la
  // persistencia viven aqui (dueño unico); el control se renderiza en
  // WordCountBar y se aplica al papel via la prop `zoom` de ContinuousDocument.
  const [zoom, setZoom] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem('editor.zoom'));
      return (v >= 0.5 && v <= 2) ? v : 1;
    } catch { return 1; }
  });
  const applyZoom = (next) => {
    const z = Math.min(2, Math.max(0.5, Math.round(next * 10) / 10));
    setZoom(z);
    try { localStorage.setItem('editor.zoom', String(z)); } catch { /* ignore */ }
  };
  const [sheetsCollapsed, setSheetsCollapsed] = useState(() => {
    try { return localStorage.getItem('editor.sheetsCollapsed') === '1'; } catch { return false; }
  });
  const toggleSheetsCollapsed = () => {
    setSheetsCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('editor.sheetsCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };
  const [commentsOpen, setCommentsOpen] = useState(false); // T-057: panel de notas/comentarios
  const [propertiesOpen, setPropertiesOpen] = useState(false);

  // #4: clic en una marca de comentario en el doc ABRE la columna del margen. Si
  // estaba cerrada, el evento se disparó ANTES de que la columna montara su
  // handler, así que re-despachamos tras un tick para que en ese primer clic la
  // tarjeta correspondiente se enfoque y se centre. (Debe ir DESPUÉS de declarar
  // commentsOpen: el array de deps lo evalúa en cada render → TDZ si va antes.)
  useEffect(() => {
    const openColumn = (e) => {
      if (commentsOpen) return; // ya abierta → el handler de la columna la enfoca
      setCommentsOpen(true);
      const cid = e?.detail?.commentId;
      if (cid != null) {
        setTimeout(
          () => window.dispatchEvent(new CustomEvent(OPEN_COMMENT_THREAD_EVENT, { detail: { commentId: cid } })),
          90,
        );
      }
    };
    window.addEventListener(OPEN_COMMENT_THREAD_EVENT, openColumn);
    return () => window.removeEventListener(OPEN_COMMENT_THREAD_EVENT, openColumn);
  }, [commentsOpen]);

  // El botón de comentar-tabla abre la columna e inicia composición. El timeout
  // deja que la columna monte su listener de startCommentCompose antes de dispararlo.
  useEffect(() => {
    const openAndCompose = () => {
      setCommentsOpen(true);
      setTimeout(() => window.dispatchEvent(new CustomEvent('startCommentCompose')), 60);
    };
    window.addEventListener('openCommentsAndCompose', openAndCompose);
    return () => window.removeEventListener('openCommentsAndCompose', openAndCompose);
  }, []);
  const [configOpen, setConfigOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false); // T-086: confirmar antes de cerrar sesión
  const [commandsOpen, setCommandsOpen] = useState(false);
  // T-234: modal de Flujos IA (gestión + ejecución). Reusa el orquestador de
  // macros (T-231) al ejecutar; el store propio es flowsStore.
  const [flowsOpen, setFlowsOpen] = useState(false);
  // T-212 (ítem 19): acción inicial para CommandManagement cuando se abre desde
  // el popover de plantillas del chat ({ type: 'create' } | { type: 'edit', commandId }).
  const [commandManagerAction, setCommandManagerAction] = useState(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [quickCommandModal, setQuickCommandModal] = useState(null);
  // T-034: while a macro runs, shows "Macro: paso k/N…" in the AI-work banner.
  const [macroProgress, setMacroProgress] = useState(null);
  const [floatingButtonPos, setFloatingButtonPos] = useState(null);
  const [selectedTextForAI, setSelectedTextForAI] = useState('');
  const [hasImageSelected, setHasImageSelected] = useState(false);
  const [selectedImageNode, setSelectedImageNode] = useState(null);
  const [selectedImagePos, setSelectedImagePos] = useState(null);
  const [chatContext, setChatContext] = useState('none'); // Track chat context
  const [exporting, setExporting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // T-120b: slot rotativo del header compacto (no persistente — arranca en 'export').
  const [headerSlot, setHeaderSlot] = useState('export');
  const [tableOptionsOpen, setTableOptionsOpen] = useState(false);
  const [selectedTableForOptions, setSelectedTableForOptions] = useState(null);
  // T-128: diálogo de reordenar hojas (móvil/tablet — el <select> solo elige).
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  // T-246: cita pendiente de flashear tras cambiar de hoja (ir a la fuente)
  const [pendingFlashQuote, setPendingFlashQuote] = useState(null);


  // Markdown helpers: detect and convert Markdown tables to HTML before inserting
  const unwrapCodeFence = (s) => {
    if (!s) return '';
    const m = s.match(/^```[a-zA-Z]*\s*([\s\S]*?)\s*```$/m);
    return m ? m[1].trim() : s;
  };
  // T-022: Markdown-table detect/convert helpers were removed from the quick-command
  // flow. The unified insertion path (useTextInsertion + classifyInsertion, T-018)
  // already renders Markdown tables into native table nodes at insert time, so the
  // quick command now passes the raw Markdown string straight through.

  const editorRef = useRef(null);
  const modalDraggingRef = useRef(false);
  const savedSelectionRef = useRef(null);
  // T-053: pos del botón flotante de IA al abrir el modal de acción rápida, para
  // restaurarlo al cerrar si la selección sigue viva (el botón no debe desaparecer
  // mientras haya un elemento seleccionable por IA).
  const lastFloatingButtonPosRef = useRef(null);
  // T-035.1: sticky properties panel. Remembers the doc position of the image the
  // panel is bound to so a *transient* loss of NodeSelection (e.g. focus moving to
  // a panel control) does NOT tear the panel down. Only an explicit close, the
  // node ceasing to exist, or a deliberate in-editor text selection closes it.
  const rememberedImagePosRef = useRef(null);
  // T-042.img: once the user explicitly closes the properties panel, the 100ms
  // poll must NOT immediately re-open it while the same image is still selected
  // (that resurrection is what looked like "N panels stacking"). We store the
  // pos the user closed on and suppress auto-open until the selection changes.
  const suppressAutoOpenPosRef = useRef(null);
  // T-022: mirror the chat sidebar's active session id so quick-command actions
  // can persist their exchange into the SAME chat session the user sees.
  const activeChatSessionRef = useRef(null);

  // Bumped after an AI server-side write to remount the spreadsheet grid so the
  // new cells show (the grid reads content only on mount). See refetchSheetsAndApply.
  const [spreadsheetReloadNonce, setSpreadsheetReloadNonce] = useState(0);

  // T-026: lock the editor while an /ai/chat request is in flight (see hook).
  const { locked: aiLocked, runLocked, cancel: cancelAiRequest } =
    useEditorLock(() => editorRef.current?.getEditor?.());

  // T-028 save-before: flush any pending debounced autosave (and await it) BEFORE
  // locking + sending the chat request, so the AI reads the latest content the
  // user typed. Then run inside the editor lock exactly as before.
  const runLockedWithFlush = useCallback(
    async (requestFn, timeoutMs) => {
      try {
        await editorRef.current?.flushPendingUpdate?.();
      } catch (e) {
        // A failed flush must not block the AI request; log and continue.
        console.error('Flush before AI request failed:', e);
      }
      // timeoutMs (optional) overrides the lock's fail-safe for this acquisition
      // (T-034: macros pass N*120000 so the lock covers all N steps).
      return runLocked(requestFn, timeoutMs);
    },
    [runLocked]
  );

  // T-246: refresca los chips de referencias en el editor para la hoja activa.
  // Best-effort: si el endpoint falla o el editor no está listo, no se rompe nada.
  const refreshReferences = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/documents/${documentId}/references`);
      const refs = (r.data?.references || []).filter((x) => x.target_sheet_id === activeSheetId);
      const ed = editorRef.current?.getEditor?.();
      if (ed?.commands?.setReferences) ed.commands.setReferences(refs);
    } catch { /* best-effort: sin referencias no se rompe el editor */ }
  }, [documentId, activeSheetId]);

  // T-028 refetch-after: pull the server's sheets and re-apply the active one to
  // the (still-locked) editor. Called BEFORE the lock releases so there is no
  // focus/apply conflict. Applies to the active sheet only when it was touched.
  const refetchSheetsAndApply = useCallback(
    async (modifiedIds) => {
      const resp = await axiosInstance.get(`/documents/${documentId}/sheets`);
      const sorted = resp.data.sort((a, b) => a.order - b.order);
      setSheets(sorted);
      const active = sorted.find((s) => s.id === activeSheetId);
      const touchedActive = !modifiedIds || modifiedIds.includes(activeSheetId);
      if (active && touchedActive) {
        // A spreadsheet grid reads its content only on mount and has no
        // applyServerContent (that ref lives on ContinuousDocument). Bump a nonce
        // to force a remount so an AI cell-write actually shows; text sheets keep
        // the in-place apply. Only fires on server refetch, never on local edits,
        // so typing is never interrupted.
        if (active.content_type === 'spreadsheet') {
          setSpreadsheetReloadNonce((n) => n + 1);
        } else {
          editorRef.current?.applyServerContent?.(active.content);
        }
      }
      // T-246: refresca chips de referencias (la IA puede haber creado referencias
      // via tool — el tool devuelve modified_sheet_id y este camino ya corre).
      refreshReferences();
      return sorted;
    },
    [documentId, activeSheetId, refreshReferences]
  );

  // T-028: after an AI response that wrote server-side, refetch + apply, then toast.
  const handleDocumentModified = useCallback(
    async (modifiedIds) => {
      await refetchSheetsAndApply(modifiedIds);
      const n = (modifiedIds && modifiedIds.length) || 0;
      toast.success(`La IA modificó ${n} hoja(s)`);
    },
    [refetchSheetsAndApply]
  );

  // T-246: ir a la hoja origen de una referencia y flashear el bloque citado.
  // El mecanismo de cambio de hoja es setActiveSheetId (el MISMO que usan
  // SheetTabs via onSelectSheet={setActiveSheetId}).
  const handleGoToSource = useCallback((ref) => {
    if (!ref) return;
    setPendingFlashQuote(ref.source_quote || null);
    if (ref.source_sheet_id && ref.source_sheet_id !== activeSheetId) {
      setActiveSheetId(ref.source_sheet_id);
    }
  }, [activeSheetId]);

  // T-248: flashea el bloque citado tras cambiar de hoja. El ContinuousDocument
  // REMONTA por key={doc-...} y su contenido llega async, así que un setTimeout
  // fijo (T-246) aplicaba el comando sobre un editor inexistente o vacío y el
  // resaltado NO aparecía. Ahora se reintenta hasta que el comando confirma que
  // encontró el bloque (devuelve false mientras no esté), con techo de ~4s.
  useEffect(() => {
    if (!pendingFlashQuote) return;
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      const ed = editorRef.current?.getEditor?.();
      const ok = ed?.commands?.flashReferenceBlock?.(pendingFlashQuote);
      if (ok || tries >= 20) clearInterval(iv);
    }, 200);
    return () => clearInterval(iv);
  }, [activeSheetId, pendingFlashQuote]);

  // T-246: dispara refreshReferences al cambiar de hoja (el editor remonta por
  // key={doc-...}; refreshReferences ya maneja el caso de editor no-listo).
  useEffect(() => {
    if (!activeSheetId) return;
    refreshReferences();
  }, [activeSheetId, refreshReferences]);

  // T-246: listener 'referencesUpdated' → refresca chips en el editor.
  useEffect(() => {
    const handler = () => refreshReferences();
    window.addEventListener('referencesUpdated', handler);
    return () => window.removeEventListener('referencesUpdated', handler);
  }, [refreshReferences]);

  // T-246: listeners de eventos de referencias (ir a la fuente / cerrar tarjeta).
  useEffect(() => {
    const onGoToSource = (e) => handleGoToSource(e.detail?.reference);
    const onCardClosed = () => {
      setPendingFlashQuote(null);
      editorRef.current?.getEditor?.()?.commands?.clearReferenceFlash?.();
    };
    window.addEventListener('referencesGoToSource', onGoToSource);
    window.addEventListener('referenceCardClosed', onCardClosed);
    return () => {
      window.removeEventListener('referencesGoToSource', onGoToSource);
      window.removeEventListener('referenceCardClosed', onCardClosed);
    };
  }, [handleGoToSource]);

  useEffect(() => {
    fetchDocument();
    fetchSheets();
    fetchGroups();
  }, [documentId]);

  const toggleChatSize = () => {
    setIsExpanded(!isExpanded);
  };



  // Listen for modal drag events
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleDragStart = () => {
      modalDraggingRef.current = true;
    };
    const handleDragEnd = () => {
      modalDraggingRef.current = false;
    };

    if (window && window.addEventListener) {
      window.addEventListener('modalDragStart', handleDragStart);
      window.addEventListener('modalDragEnd', handleDragEnd);
    }

    return () => {
      if (typeof window !== 'undefined' && window && window.removeEventListener) {
        window.removeEventListener('modalDragStart', handleDragStart);
        window.removeEventListener('modalDragEnd', handleDragEnd);
      }
    };
  }, []);


  // Refocus editor on demand (e.g., after picking a command from the Select)
  useEffect(() => {
    const handler = () => {
      const editor = editorRef.current?.getEditor?.();
      if (!editor) return;
      // Restore the saved selection if we have one
      if (savedSelectionRef.current) {
        try {
          editor.view.dispatch(editor.state.tr.setSelection(savedSelectionRef.current));
        } catch {}
      }
      editor.view.focus();
    };
    window.addEventListener('refocusEditor', handler);
    return () => window.removeEventListener('refocusEditor', handler);
  }, []);


  // Global click handler to detect when text is deselected
  // Reason: Only deselect when clicking on gray area or empty document space, not UI elements
  const handleGlobalClick = useCallback((e) => {
    // Safety check: e.target can be null after setTimeout delay
    if (!e.target) return;

    // T-040 Bug 1: never collapse an active NodeSelection (e.g. a selected
    // image). Clicking non-input zones of the properties panel used to fall
    // through to setTextSelection and kill the selection → the panel stayed
    // open but the blue border/handles vanished. Guard the whole handler.
    const activeEditor = editorRef.current?.getEditor?.();
    if (activeEditor?.state?.selection instanceof NodeSelection) {
      return;
    }

    // IMPORTANT: Don't clear selection when clicking on UI elements
    // Check if click is on any interactive UI element or the editor itself
    const isUIElement = e.target.closest('button') ||
                       e.target.closest('input') ||
                       e.target.closest('select') ||
                       e.target.closest('textarea') ||
                       e.target.closest('[role="button"]') ||
                       e.target.closest('[role="combobox"]') ||
                       e.target.closest('[role="listbox"]') ||
                       e.target.closest('[role="option"]') ||
                       e.target.closest('[role="menuitem"]') ||
                       e.target.closest('[data-testid="quick-command-modal"]') ||
                       e.target.closest('[data-testid="quick-command-select-content"]') ||
                       e.target.closest('[data-testid="floating-ai-button"]') ||
                       e.target.closest('[data-testid="chat-sidebar"]') ||
                       e.target.closest('[data-properties-panel]') ||
                       e.target.closest('.editor-toolbar') ||
                       e.target.closest('[data-testid="tiptap-editor"]') ||
                       e.target.closest('.ProseMirror') ||
                       modalDraggingRef.current ||
                       quickCommandModal;

    if (isUIElement) {
      // Click is on UI element or editor, don't clear selection
      return;
    }

    // If we reach here, click is on gray area or outside all UI elements
    // Clear overlays and selected text
    setFloatingButtonPos(null);
    setSelectedTextForAI({ text: '', images: [], html: '', hasTables: false });
    window.dispatchEvent(new CustomEvent('textSelected', { detail: { text: '', images: [], html: '', hasTables: false } }));

    // Collapse editor selection so the inline AI button (decoration) disappears
    const editor = editorRef.current?.getEditor?.();
    if (editor && editor.state && editor.state.selection) {
      try {
        const pos = editor.state.selection.to;
        editor.chain().setTextSelection(pos).run();
      } catch {
        // ignore
      }
    }
  }, [quickCommandModal]);

  useEffect(() => {
    // Safety check for window and document
    // Reason: Avoid conflicts with React state variable named 'document'
    if (typeof window === 'undefined' || !window.document) return;

    // Use a slight delay to let TipTap process its selection first
    const handleGlobalClickDelayed = (e) => {
      setTimeout(() => handleGlobalClick(e), 50);
    };

    // Use window.document to access the global DOM document object
    // (not the React state variable named 'document')
    const doc = window.document;
    if (doc && doc.addEventListener) {
      doc.addEventListener('mousedown', handleGlobalClickDelayed);
    }

    return () => {
      if (typeof window !== 'undefined' && doc && doc.removeEventListener) {
        doc.removeEventListener('mousedown', handleGlobalClickDelayed);
      }
    };
  }, [handleGlobalClick]);

  // ESC key handler to deselect text
  // Reason: Allow users to deselect text by pressing ESC key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        // Clear overlays and selected text
        setFloatingButtonPos(null);
        setSelectedTextForAI({ text: '', images: [], html: '', hasTables: false });
        window.dispatchEvent(new CustomEvent('textSelected', {
          detail: { text: '', images: [], html: '', hasTables: false }
        }));

        // Collapse editor selection
        const editor = editorRef.current?.getEditor?.();
        if (editor && editor.state && editor.state.selection) {
          try {
            const pos = editor.state.selection.to;
            editor.chain().setTextSelection(pos).run();
          } catch {
            // ignore
          }
        }
      }
    };

    window.document.addEventListener('keydown', handleEscKey);
    return () => {
      window.document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  const closeProperties = useCallback(() => {
    // Only hide the column. Keep the bound image (node/pos) so reopening shows
    // its properties immediately instead of an empty panel.
    setPropertiesOpen(false);
    setChatOpen(true);
  }, []);

  // Track which image is selected (to bind the panel + show the floating button).
  // IMPORTANT: this poll NEVER closes the panel. Closing is explicit — the X, Esc,
  // or a click outside the image/panel (handlers below). A resize drag perturbs
  // the ProseMirror selection, so any selection-driven close would tear the panel
  // down mid-resize; decoupling it is what finally makes it stable.
  useEffect(() => {
    const isImageType = (node) =>
      Boolean(node) && (node.type.name === 'image' || node.type.name === 'resizableImage');

    const checkImageSelection = () => {
      const api = editorRef.current;
      if (!api || !api.getSelectedNode) return;
      const editor = api.getEditor?.();
      const selectedNode = api.getSelectedNode();
      const selectedPos = api.getSelectedNodePos?.();
      if (isImageType(selectedNode)) {
        rememberedImagePosRef.current = selectedPos;
        if (!hasImageSelected) setHasImageSelected(true);
        setSelectedImageNode(selectedNode);
        setSelectedImagePos(selectedPos);
        return;
      }
      // No LIVE image NodeSelection → the floating "image properties" button must
      // HIDE (it should show ONLY while an image is actually selected). This is
      // independent of the sticky-panel binding kept below: a remembered image
      // that still exists keeps the PANEL bound, but does not resurrect the button
      // once the user has moved to a text selection.
      if (hasImageSelected) setHasImageSelected(false);
      const pos = rememberedImagePosRef.current;
      if (pos != null && editor) {
        // safeNodeAt: la pos recordada puede quedar FUERA del doc si éste se
        // encogió; nodeAt directo lanzaba RangeError cada 150ms (T-080).
        const n = safeNodeAt(editor.state.doc, pos);
        if (isImageType(n)) {
          setSelectedImageNode(n);
          setSelectedImagePos(pos);
          return;
        }
        // The remembered image is gone (deleted): drop the binding.
        rememberedImagePosRef.current = null;
        setSelectedImageNode(null);
      }
    };

    checkImageSelection();
    const interval = setInterval(checkImageSelection, 150);
    return () => clearInterval(interval);
  }, [hasImageSelected]);

  // The floating "open properties" button (rendered on the selected image) fires
  // this event. Opening is explicit and the panel stays open until the user
  // closes it (X / Esc / click outside the image).
  useEffect(() => {
    const openProps = (e) => {
      setPropertiesOpen(true);
      setChatOpen(false);
      setTableOptionsOpen(false);
      // Bind directly to the image the button belongs to (robust to the live
      // selection not being a NodeSelection after a resize).
      const pos = e?.detail?.pos;
      if (pos != null) {
        const editor = editorRef.current?.getEditor?.();
        const n = safeNodeAt(editor?.state?.doc, pos);
        if (n && (n.type.name === 'image' || n.type.name === 'resizableImage')) {
          rememberedImagePosRef.current = pos;
          setSelectedImageNode(n);
          setSelectedImagePos(pos);
        }
      }
    };
    window.addEventListener('openImageProperties', openProps);
    return () => window.removeEventListener('openImageProperties', openProps);
  }, []);

  // Close the properties column on a click outside the image / its handles /
  // button / the panel itself ("si deselecciona la imagen la columna desaparece").
  useEffect(() => {
    if (!propertiesOpen) return;
    const onDocMouseDown = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      if (
        t.closest('.resizable-image-wrapper') ||   // the image, its handles, its button
        t.closest('[data-properties-panel]') ||     // the panel and its controls
        t.closest('[data-radix-popper-content-wrapper]') // dropdowns/pickers opened from the panel
      ) {
        return;
      }
      closeProperties();
    };
    // NOTE: `document` is shadowed by the document-data state in this component,
    // so we attach to `window` (events bubble/capture through it).
    window.addEventListener('mousedown', onDocMouseDown, true);
    return () => window.removeEventListener('mousedown', onDocMouseDown, true);
  }, [propertiesOpen, closeProperties]);

  // Esc closes the properties column too.
  useEffect(() => {
    if (!propertiesOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') closeProperties(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [propertiesOpen, closeProperties]);

  // Tell the image NodeView which image the panel is bound to, so it keeps its
  // selection chrome (border + drag handles) visible while the user interacts
  // with the panel controls (which blur the editor and would otherwise hide it).
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('imagePropsPanelBind', {
      detail: { pos: propertiesOpen ? selectedImagePos : null },
    }));
  }, [propertiesOpen, selectedImagePos]);

  const fetchDocument = async () => {
    try {
      const response = await axiosInstance.get(`/documents/${documentId}`);
      setDocument(response.data);
    } catch (error) {
      console.error('Error fetching document:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al cargar documento');
      }
      navigate('/dashboard');
    }
  };

  const fetchSheets = async () => {
    try {
      const response = await axiosInstance.get(`/documents/${documentId}/sheets`);

      const sortedSheets = response.data.sort((a, b) => a.order - b.order);
      setSheets(sortedSheets);

      // Reason: Create first sheet if none exists
      if (sortedSheets.length === 0) {
        const newSheet = await createNewSheet();
        if (newSheet) {
          setActiveSheetId(newSheet.id);
        }
      } else {
        // Set first sheet as active if none is selected
        if (!activeSheetId) {
          setActiveSheetId(sortedSheets[0].id);
        }
      }

    } catch (error) {
      console.error('Error fetching sheets:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al cargar hojas');
      }
    } finally {
      setLoading(false);
    }
  };



  // T-133: maxPos del espacio de orden compartido (huérfanas + grupos).
  const computeMaxPos = () => {
    const existingIds = new Set(groups.map((g) => g.id));
    const orphanOrders = sheets
      .filter((s) => !s.group_id || !existingIds.has(s.group_id))
      .map((s) => s.order ?? 0);
    const groupOrders = groups.map((g) => g.group_order ?? 0);
    return Math.max(-1, ...orphanOrders, ...groupOrders);
  };

  // T-133: reordenar la fila única (hojas huérfanas + chips de grupo).
  // Recibe rowItems ya reindexados; persiste order (sheets) y group_order (groups).
  // Solo reindexa rowItems — NUNCA el arreglo completo de sheets (las agrupadas
  // viven en el dropdown de su grupo y su order no se toca).
  const onReorderRow = async (rowItems) => {
    try {
      await Promise.all(
        rowItems.map((item) =>
          item.type === 'sheet'
            ? axiosInstance.put(`/sheets/${item.data.id}`, { order: item.pos })
            : axiosInstance.put(`/groups/${item.data.id}`, { group_order: item.pos })
        )
      );
      // Actualiza el order de las huérfanas; deja las agrupadas intactas.
      setSheets((prev) =>
        prev.map((s) => {
          const found = rowItems.find((it) => it.type === 'sheet' && it.data.id === s.id);
          return found ? { ...s, order: found.pos } : s;
        })
      );
      setGroups((prev) =>
        prev.map((g) => {
          const found = rowItems.find((it) => it.type === 'group' && it.data.id === g.id);
          return found ? { ...g, group_order: found.pos } : g;
        })
      );
      toast.success('Hojas reorganizadas');
    } catch (error) {
      console.error('Error reordering row:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al reorganizar hojas');
      }
    }
  };

  // Reason: Create a new sheet (like adding a new page in Word). T-049: `type`
  // selects a text sheet (TipTap json, default) or a spreadsheet sheet.
  const createNewSheet = async (type = 'text') => {
    try {
      // T-133: la hoja nueva es huérfana y debe caer al FINAL de la fila
      // (espacio de orden compartido), no en sheets.length (que podría colisionar).
      const newOrder = computeMaxPos() + 1;
      const isSpreadsheet = type === 'spreadsheet';
      const response = await axiosInstance.post(
        `/documents/${documentId}/sheets`,
        {
          name: isSpreadsheet
            ? `Cálculo ${newOrder + 1}`
            : `Hoja ${newOrder + 1}`,
          content: isSpreadsheet
            ? { type: 'spreadsheet', rows: 20, cols: 8, cells: {} }
            : {
                type: 'doc',
                content: [{
                  type: 'paragraph',
                  content: []
                }]
              },
          content_type: isSpreadsheet ? 'spreadsheet' : 'json',
          order: newOrder
        }
      );

      const newSheets = [...sheets, response.data];
      setSheets(newSheets);

      // Reason: Switch to the new sheet immediately
      setActiveSheetId(response.data.id);

      toast.success('Nueva hoja creada');

      return response.data;
    } catch (error) {
      console.error('Create sheet error:', error);
      if (error.response?.status !== 401) {
        // T-136: nombre duplicado -> mostrar el detalle del backend (409).
        toast.error(
          error.response?.status === 409
            ? (error.response.data?.detail || 'Ya existe una hoja con ese nombre')
            : 'Error al crear hoja'
        );
      }
    }
  };

  // T-056: importar un archivo (Word/md/pdf/txt) como nueva hoja. Sube el File al
  // endpoint server-side, que parsea a doc TipTap y crea la hoja; luego refresca.
  const importSheet = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    const toastId = toast.loading(`Importando ${file.name}…`);
    try {
      const resp = await axiosInstance.post(
        `/documents/${documentId}/import`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setSheets((prev) => [...prev, resp.data]);
      setActiveSheetId(resp.data.id);
      toast.success('Documento importado', { id: toastId });
      return resp.data;
    } catch (error) {
      console.error('Import error:', error);
      if (error.response?.status !== 401) {
        const detail = error.response?.data?.detail || 'Error al importar el documento';
        toast.error(detail, { id: toastId });
      } else {
        toast.dismiss(toastId);
      }
    }
  };

  // Reason: Update specific sheet content.
  // T-028: send the sheet's last known ``updated_at`` as an optimistic-lock
  // precondition. On 409 (the document changed in another session/the AI wrote
  // it) refetch, apply the server content, and toast — server wins. Legacy rows
  // without a stamp fall back to last-write-wins (no precondition sent).
  const updateSheetContent = async (sheetId, content, contentType = 'json') => {
    const known = sheets.find((s) => s.id === sheetId);
    const expected = known?.updated_at;
    try {
      const resp = await axiosInstance.put(
        `/sheets/${sheetId}`,
        {
          content: content,
          content_type: contentType,
          ...(expected ? { expected_updated_at: expected } : {})
        }
      );

      // Track the new stamp so the NEXT autosave sends a fresh precondition.
      setSheets((prev) => prev.map(s =>
        s.id === sheetId
          ? { ...s, content, updated_at: resp.data?.updated_at ?? s.updated_at }
          : s
      ));
    } catch (error) {
      if (error.response?.status === 409) {
        await refetchSheetsAndApply([sheetId]);
        toast.info('El documento cambió en otra sesión — recargado');
      } else {
        toast.error('Error al guardar');
        console.error('Save error:', error);
      }
    }
  };

  // Reason: Delete a sheet
  const deleteSheet = async (sheetId) => {
    if (sheets.length === 1) {
      toast.error('No puedes eliminar la última hoja');
      return;
    }

    try {
      await axiosInstance.delete(`/sheets/${sheetId}`);

      const newSheets = sheets.filter(s => s.id !== sheetId);
      setSheets(newSheets);

      // If deleted sheet was active, select another one
      if (activeSheetId === sheetId && newSheets.length > 0) {
        setActiveSheetId(newSheets[0].id);
      }

      toast.success('Hoja eliminada');
    } catch (error) {
      console.error('Error deleting sheet:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al eliminar hoja');
      }
    }
  };

  // Reason: Reorder sheets
  const reorderSheets = async (reorderedSheets) => {
    try {
      // Update order in backend
      await Promise.all(
        reorderedSheets.map(sheet =>
          axiosInstance.put(`/sheets/${sheet.id}`, { order: sheet.order })
        )
      );

      setSheets(reorderedSheets);
      toast.success('Hojas reorganizadas');
    } catch (error) {
      console.error('Error reordering sheets:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al reorganizar hojas');
      }
    }
  };

  const handleExport = async (format) => {
    if (!documentId || exporting) {
      return;
    }

    setExporting(true);
    try {
      const response = await axiosInstance.get(
        `/documents/${documentId}/export`,
        {
          params: { format },
          responseType: 'blob'
        }
      );

      const normalizedFormat = format === 'word' ? 'docx' : format;
      const extensionMap = {
        docx: 'docx',
        pdf: 'pdf',
        txt: 'txt',
        text: 'txt',
        md: 'md',
        markdown: 'md'
      };
      const extension = extensionMap[normalizedFormat] || normalizedFormat;

      const fallbackMime = {
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        pdf: 'application/pdf',
        txt: 'text/plain; charset=utf-8',
        md: 'text/markdown; charset=utf-8'
      }[extension] || 'application/octet-stream';

      const mimeType = response.headers?.['content-type'] || fallbackMime;
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      const rawTitle = document?.title || 'documento';
      const sanitizedTitle = rawTitle.replace(/[\\/:*?"<>|]+/g, '').trim() || 'documento';
      const timestamp = new Date().toISOString().replace(/[:T]/g, '-').split('.')[0];

      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${sanitizedTitle}-${timestamp}.${extension}`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Documento exportado como ${extension.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting document:', error);
      toast.error('Error al exportar documento');
    } finally {
      setExporting(false);
    }
  };

  // #14: export the ACTIVE spreadsheet sheet as CSV or XLSX (computed values).
  const handleExportSheet = async (format) => {
    if (!activeSheetId || exporting) {
      return;
    }

    setExporting(true);
    try {
      const response = await axiosInstance.get(
        `/sheets/${activeSheetId}/export`,
        {
          params: { format },
          responseType: 'blob'
        }
      );

      const extension = format === 'excel' ? 'xlsx' : format;
      const fallbackMime = {
        csv: 'text/csv; charset=utf-8',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }[extension] || 'application/octet-stream';

      const mimeType = response.headers?.['content-type'] || fallbackMime;
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      const rawName = activeSheet?.name || 'hoja';
      const sanitizedName = rawName.replace(/[\\/:*?"<>|]+/g, '').trim() || 'hoja';

      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${sanitizedName}.${extension}`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Hoja exportada como ${extension.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting sheet:', error);
      toast.error('Error al exportar la hoja');
    } finally {
      setExporting(false);
    }
  };

  // Reason: Rename a sheet
  const renameSheet = async (sheetId, newName) => {
    try {
      await axiosInstance.put(`/sheets/${sheetId}`, { name: newName });

      setSheets(sheets.map(s =>
        s.id === sheetId
          ? { ...s, name: newName }
          : s
      ));

      toast.success('Hoja renombrada');
    } catch (error) {
      console.error('Error renaming sheet:', error);
      if (error.response?.status !== 401) {
        // T-136: nombre duplicado -> mostrar el detalle del backend (409).
        toast.error(
          error.response?.status === 409
            ? (error.response.data?.detail || 'Ya existe una hoja con ese nombre')
            : 'Error al renombrar hoja'
        );
      }
    }
  };

  // R2: marca/desmarca una hoja como FUENTE (solo lectura para la IA). Optimista;
  // revierte el estado local si el PUT falla.
  const toggleSource = async (sheet) => {
    const next = !sheet.is_source;
    setSheets((prev) => prev.map((s) => (s.id === sheet.id ? { ...s, is_source: next } : s)));
    try {
      await axiosInstance.put(`/sheets/${sheet.id}`, { is_source: next });
      toast.success(
        next ? `«${sheet.name}» marcada como fuente` : `«${sheet.name}» ya no es fuente`
      );
    } catch (error) {
      setSheets((prev) => prev.map((s) => (s.id === sheet.id ? { ...s, is_source: !next } : s)));
      if (error.response?.status !== 401) toast.error('Error al cambiar la hoja fuente');
    }
  };

  // T-127: grupos de hojas (Fase 1 — asignar por botón, sin drag).
  const fetchGroups = async () => {
    try {
      const resp = await axiosInstance.get(`/documents/${documentId}/groups`);
      const sorted = (resp.data || []).sort((a, b) => (a.group_order ?? 0) - (b.group_order ?? 0));
      setGroups(sorted);
    } catch (error) {
      console.error('Error fetching groups:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al cargar grupos');
      }
    }
  };

  const createGroup = async () => {
    try {
      const resp = await axiosInstance.post(`/documents/${documentId}/groups`, {});
      const newGroup = resp.data;
      // T-133: apendar al final de la fila (espacio de orden compartido).
      const groupOrder = computeMaxPos() + 1;
      await axiosInstance.put(`/groups/${newGroup.id}`, { group_order: groupOrder });
      setGroups((prev) => [...prev, { ...newGroup, group_order: groupOrder }]);
      toast.success('Grupo creado');
    } catch (error) {
      console.error('Error creating group:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al crear grupo');
      }
    }
  };

  const updateGroup = async (groupId, patch) => {
    const prev = groups;
    // Optimista: aplica el patch al estado local antes de persistir.
    setGroups((gs) => gs.map((g) => (g.id === groupId ? { ...g, ...patch } : g)));
    try {
      await axiosInstance.put(`/groups/${groupId}`, patch);
    } catch (error) {
      setGroups(prev);
      console.error('Error updating group:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al actualizar grupo');
      }
    }
  };

  const deleteGroup = async (groupId) => {
    try {
      await axiosInstance.delete(`/groups/${groupId}`);
      setGroups((prev) => prev.filter((g) => g.id !== groupId));
      // Las hojas del grupo quedan huérfanas (group_id NULL en el backend).
      await fetchSheets();
      toast.success('Grupo eliminado');
    } catch (error) {
      console.error('Error deleting group:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al eliminar grupo');
      }
    }
  };

  const setSheetGroup = async (sheetId, groupId) => {
    const prev = sheets;
    // Optimista: actualiza el group_id de la hoja en el estado local.
    setSheets((ss) => ss.map((s) => (s.id === sheetId ? { ...s, group_id: groupId } : s)));
    try {
      await axiosInstance.put(`/sheets/${sheetId}/group`, { group_id: groupId });
    } catch (error) {
      setSheets(prev);
      console.error('Error assigning sheet to group:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al asignar hoja');
      }
      return;
    }
    // T-133: al desagrupar, la hoja reentra al FINAL de la fila (no en una
    // pos vieja/stale en medio). computeMaxPos usa el estado PRE-optimista
    // (la hoja seguía agrupada, así que su order no cuenta para el max).
    if (groupId === null) {
      try {
        const newOrder = computeMaxPos() + 1;
        await axiosInstance.put(`/sheets/${sheetId}`, { order: newOrder });
        setSheets((ss) => ss.map((s) => (s.id === sheetId ? { ...s, order: newOrder } : s)));
      } catch (error) {
        console.error('Error updating sheet order after ungroup:', error);
        if (error.response?.status !== 401) {
          toast.error('Error al reposicionar la hoja');
        }
      }
    }
  };

  // R1: descarga el registro de cambios de la IA del documento como CSV.
  const handleExportChanges = async () => {
    if (!documentId || exporting) return;
    setExporting(true);
    try {
      const response = await axiosInstance.get(
        `/documents/${documentId}/changes/export`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `cambios_${documentId}.csv`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Registro de cambios exportado');
    } catch (error) {
      console.error('Error exporting changes:', error);
      toast.error('Error al exportar el registro de cambios');
    } finally {
      setExporting(false);
    }
  };

  // #11: descarga todos los comentarios del documento (pendientes/resueltos/procesados) como CSV.
  const handleExportComments = async () => {
    if (!documentId || exporting) return;
    setExporting(true);
    try {
      const response = await axiosInstance.get(
        `/documents/${documentId}/comments/export`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `comentarios_${documentId}.csv`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Comentarios exportados');
    } catch (error) {
      console.error('Error exporting comments:', error);
      toast.error('Error al exportar los comentarios');
    } finally {
      setExporting(false);
    }
  };

  // T-049: toggle the active sheet's page orientation (portrait<->landscape) and
  // persist it. Orientation lives on the sheet, so the toggle re-renders the
  // paper via the `orientation` derived from `activeSheet`.
  const toggleOrientation = async () => {
    const active = sheets.find((s) => s.id === activeSheetId);
    if (!active) return;
    const next = (active.orientation || 'portrait') === 'landscape' ? 'portrait' : 'landscape';
    // Optimistic UI: flip locally first, then persist.
    setSheets((prev) => prev.map(s => s.id === activeSheetId ? { ...s, orientation: next } : s));
    try {
      await axiosInstance.put(`/sheets/${activeSheetId}`, { orientation: next });
    } catch (error) {
      console.error('Error updating orientation:', error);
      // Roll back on failure.
      setSheets((prev) => prev.map(s => s.id === activeSheetId ? { ...s, orientation: active.orientation || 'portrait' } : s));
      if (error.response?.status !== 401) {
        toast.error('Error al cambiar la orientación');
      }
    }
  };




  const handleTextSelection = (selectionContent, position, type) => {
    // Handle both old format (string) and new format (object with text, images, html, hasTables)
    const content = typeof selectionContent === 'string'
      ? { text: selectionContent, images: [], html: '', hasTables: false }
      : selectionContent;

    // Check if we have any content to show
    const hasText = content.text && content.text.trim().length > 0;
    const hasImages = content.images && content.images.length > 0;
    const hasTables = content.hasTables || false;
    const hasContent = hasText || hasImages || hasTables;

    // Show appropriate UI based on context
    if (hasContent) {
      setFloatingButtonPos(position);

      // Store complete content (text, images, html, and tables)
      setSelectedTextForAI(content);
    } else {
      // No content selected
      setFloatingButtonPos(null);
      setSelectedTextForAI({ text: '', images: [], html: '', hasTables: false });
    }

    // #8: texto del bloque top-level donde esta el caret (para insertar aqui sin citar texto).
    let cursorBlockText = '';
    try {
      const _ed = editorRef.current?.getEditor?.();
      if (_ed && _ed.state && _ed.state.selection) {
        const $from = _ed.state.selection.$from;
        const blockNode = $from.depth >= 1 ? $from.node(1) : $from.parent;
        cursorBlockText = (blockNode?.textContent || '').trim().slice(0, 300);
      }
    } catch {}

    // Always dispatch text selection event (for chat context - backward compatible)
    // Include HTML for complete context
    window.dispatchEvent(new CustomEvent('textSelected', {
      detail: {
        text: content.text || '',
        images: content.images || [],
        html: content.html || '',
        hasTables: content.hasTables || false,
        blockText: cursorBlockText
      }
    }));
  };

  const openQuickCommandModal = () => {
    // Save the current editor selection before opening modal
    if (editorRef.current && editorRef.current.getEditor) {
      const editor = editorRef.current.getEditor();
      if (editor) {
        savedSelectionRef.current = editor.state.selection;
      }
    }

    // Determine an anchor position: prefer floating button pos; fallback to selection coords
    let anchorTop = floatingButtonPos?.top ?? null;
    let anchorLeft = floatingButtonPos?.left ?? null;
    // Convert container-relative to viewport-relative if available
    if (anchorTop != null && anchorLeft != null) {
      const rect = editorRef.current?.getScrollContainer?.()?.getBoundingClientRect?.();
      if (rect) {
        anchorTop = anchorTop + rect.top;
        anchorLeft = anchorLeft + rect.left;
      }
    }
    if (anchorTop == null || anchorLeft == null) {
      const editor = editorRef.current?.getEditor?.();
      const coords = editor?.view?.coordsAtPos?.(editor?.state?.selection?.to);
      if (coords) {
        anchorTop = coords.top + window.pageYOffset;
        anchorLeft = coords.left + window.pageXOffset;
      } else {
        anchorTop = Math.round(window.innerHeight / 2);
        anchorLeft = Math.round(window.innerWidth / 2);
      }
    }

    // T-053: la ventana se abre SIEMPRE al 10% del top del viewport (antes se
    // anclaba al botón/selección y, si la selección estaba baja, ~40% quedaba
    // fuera del viewport). El eje horizontal sigue anclado (acotado).
    const centeredPosition = {
      top: Math.round(window.innerHeight * 0.10),
      left: Math.max(50, Math.min(anchorLeft - 100, window.innerWidth - 550))
    };

    // Normalize selectedTextForAI to object format
    const content = typeof selectedTextForAI === 'string'
      ? { text: selectedTextForAI, images: [], html: '', hasTables: false }
      : selectedTextForAI;

    setQuickCommandModal({
      content: content,
      position: centeredPosition
    });

    // T-053: recuerda la posición del botón para restaurarla al cerrar si la
    // selección persiste; ocúltalo mientras el modal está abierto.
    lastFloatingButtonPosRef.current = floatingButtonPos;
    setFloatingButtonPos(null); // Hide button when modal opens
  };

  const handleQuickAction = async (action, instruction, originalText, callback) => {
    // T-009 BYOK: api_key is optional — the backend falls back to the platform key
    // when the user has no key configured (same rule as ChatSidebar). No hard gate.

    // If action is "process", get AI response and pass to callback
    if (action === 'process') {
      try {
        // T-028: flush-before (save pending edits) via runLockedWithFlush; and if
        // the AI wrote server-side, refetch+apply BEFORE the lock releases.
        const response = await runLockedWithFlush(async (signal) => {
          const resp = await axiosInstance.post(
            `/ai/chat`,
            {
              message: instruction,
              context: 'none',
              // T-026: scope tool reads to the sheet open in the editor.
              active_sheet_id: activeSheetId,
              document_id: documentId,
              // T-022: persist into the sidebar's active session (avoids spawning a
              // new orphan session per quick action; keeps history in one place).
              session_id: activeChatSessionRef.current || undefined,
              model: model,
              system_prompt: document?.system_prompt || undefined,
              api_key: apiKey || undefined,
              // T-033: quick actions are one-shot by design — no conversational
              // memory. Force a stateless request regardless of the chat setting.
              history_limit: 0
            },
            { signal, timeout: 120000 }
          );
          if (resp.data?.document_modified) {
            await refetchSheetsAndApply(resp.data.modified_sheet_ids || []);
          }
          return resp;
        });

        if (response.data?.document_modified) {
          const n = (response.data.modified_sheet_ids || []).length;
          toast.success(`La IA modificó ${n} hoja(s)`);
        }

        // Visibilidad §4: refresh badge + per-request charge toast.
        notifyCreditsCharged(response.data?.credits);

        const aiResponse = response.data.response;

        // T-018/T-022: pass the raw Markdown through. The unified insertion path
        // (useTextInsertion + classifyInsertion) renders Markdown tables into native
        // table nodes at insert time, so we must NOT pre-convert to a JSON object
        // here (that broke the editable textarea and crashed text.trim() on insert).
        const cleaned = unwrapCodeFence(aiResponse || '');
        if (callback) callback(cleaned);

        // T-022: mirror this exchange into the chat sidebar and adopt the session
        // the backend persisted it to, so it survives reload.
        window.dispatchEvent(new CustomEvent('quickCommandChat', {
          detail: {
            instruction,
            response: cleaned,
            sessionId: response.data.session_id || null
          }
        }));
      } catch (error) {
        console.error('Error processing with AI:', error);
        if (error.response?.status !== 401 && !error.__creditsHandled) {
          toast.error(error.response?.data?.detail || 'Error al procesar con IA');
        }
      }
    } else if (action === 'macro') {
      // T-034: sequential macro execution. Here `instruction` carries the command
      // object (with `steps`) and `originalText` is the selected text. All N steps
      // run in series INSIDE ONE editor lock (never released between steps), each
      // step POSTs /ai/chat with history_limit:0 (one-shot) and is mirrored to the
      // visible chat session. Stop-on-error: a failed step aborts the rest; steps
      // already completed are NOT reverted (no server-side rollback exists).
      const command = instruction;
      const steps = (command && command.steps) || [];
      const selectedText = originalText || '';
      const N = steps.length;
      let stepNum = 0;
      // T-231: presupuestar el lock por cada llamada facturable — 1 principal
      // por paso + hasta 3 asesores por paso — para que un paso con asesores no
      // agote el fail-safe a media macro (120s por llamada, igual que hoy por paso).
      const totalCalls = steps.reduce(
        (acc, s) => acc + 1 + Math.min((Array.isArray(s.advisors) ? s.advisors.length : 0), 3),
        0
      );
      try {
        const finalResponse = await runLockedWithFlush(async (signal) => {
          let previousResult = '';
          // Keep every step in the SAME session: seed from the sidebar's active
          // session and adopt the id the backend returns on step 0, so later steps
          // don't spawn separate orphan sessions if onSessionChange hasn't fired yet.
          let sessionId = activeChatSessionRef.current || undefined;
          for (let k = 0; k < N; k += 1) {
            if (signal.aborted) {
              throw new DOMException('Macro cancelado', 'AbortError');
            }
            stepNum = k + 1;
            setMacroProgress(`Macro: paso ${stepNum}/${N}…`);

            const message = buildStepPrompt(steps[k].instruction, k, selectedText, previousResult);
            // T-231: modelo del paso ('' = default del usuario); cae al model
            // global si el paso no declara uno propio.
            const stepModel = ((steps[k].model || '').trim()) || model;

            // T-231: consultar a cada asesor declarado (advisory:true → el backend
            // responde SIN tools) ANTES del modelo principal; sus respuestas se
            // anexan al mensaje del paso como insumo previo. Un asesor caído no
            // frena el macro — es insumo opcional.
            const advisors = (Array.isArray(steps[k].advisors) ? steps[k].advisors : []).slice(0, 3);
            const advisorNotes = [];
            for (const advisorModel of advisors) {
              if (signal.aborted) throw new DOMException('Macro cancelado', 'AbortError');
              setMacroProgress(`Macro: paso ${stepNum}/${N} — asesor ${advisorNotes.length + 1}/${advisors.length}…`);
              try {
                const aResp = await axiosInstance.post('/ai/chat', {
                  message: `Actúas como modelo ASESOR. Analiza y resuelve la siguiente instrucción; tu respuesta será insumo para el modelo principal que la ejecutará.\n\n${message}`,
                  context: 'none',
                  active_sheet_id: activeSheetId,
                  document_id: documentId,
                  session_id: sessionId,
                  model: advisorModel,
                  advisory: true,
                  system_prompt: document?.system_prompt || undefined,
                  api_key: apiKey || undefined,
                  history_limit: 0
                }, { signal, timeout: 120000 });
                notifyCreditsCharged(aResp.data?.credits);
                if (aResp.data.session_id) sessionId = aResp.data.session_id;
                const aText = unwrapCodeFence(aResp.data.response || '');
                advisorNotes.push({ model: advisorModel, text: aText });
                window.dispatchEvent(new CustomEvent('quickCommandChat', {
                  detail: { instruction: `[Asesor ${advisorModel}] paso ${stepNum}/${N}`, response: aText, sessionId: aResp.data.session_id || null }
                }));
              } catch (advisorError) {
                if (signal.aborted || advisorError.name === 'AbortError' || advisorError.code === 'ERR_CANCELED') throw advisorError;
                // T-231: asesor caído no frena el macro — es insumo opcional.
                toast.warning(`Asesor ${advisorModel} falló en paso ${stepNum}/${N}; el paso continúa sin él`);
              }
            }

            // T-231: si los asesores respondieron, anexar sus notas como insumo
            // previo al mensaje del modelo principal (que las evalúa críticamente).
            const finalMessage = advisorNotes.length > 0
              ? message + '\n\n=== RESPUESTAS DE MODELOS ASESORES (insumo previo; evalúalas críticamente e intégralas solo si aportan) ===\n' + advisorNotes.map((a, i) => `--- Asesor ${i + 1} (${a.model}) ---\n${a.text}`).join('\n\n')
              : message;

            const resp = await axiosInstance.post(
              `/ai/chat`,
              {
                message: finalMessage,
                context: 'none',
                active_sheet_id: activeSheetId,
                document_id: documentId,
                session_id: sessionId,
                model: stepModel,
                system_prompt: document?.system_prompt || undefined,
                api_key: apiKey || undefined,
                // Each macro step is one-shot; chaining is explicit via the prompt.
                history_limit: 0
              },
              { signal, timeout: 120000 }
            );

            const stepResponse = unwrapCodeFence(resp.data.response || '');
            // Visibilidad §4: each billable step refreshes the badge + toasts.
            notifyCreditsCharged(resp.data?.credits);
            // Adopt the persisted session so subsequent steps chain into it.
            if (resp.data.session_id) sessionId = resp.data.session_id;

            // Mirror every step exchange into the chat sidebar for auditability.
            // T-231: el mirror muestra el finalMessage enviado (con notas de
            // asesores si las hubo), no el message base sin anexar.
            window.dispatchEvent(new CustomEvent('quickCommandChat', {
              detail: {
                instruction: finalMessage,
                response: stepResponse,
                sessionId: resp.data.session_id || null
              }
            }));

            // If this step wrote server-side, refetch+apply BEFORE the next step
            // (still inside the lock) so the following step does not hit a 409.
            if (resp.data?.document_modified) {
              await refetchSheetsAndApply(resp.data.modified_sheet_ids || []);
              const n = (resp.data.modified_sheet_ids || []).length;
              toast.success(`La IA modificó ${n} hoja(s)`);
            }

            previousResult = stepResponse;
          }
          return previousResult;
        // Lock fail-safe budget: 120s PER CALL (totalCalls*120000). The per-request
        // axios timeout stays 120000, so a single hung call still aborts at 120s,
        // but the lock is never released mid-macro by the fail-safe. (T-231:
        // totalCalls accounts for 1 main + up to 3 advisors per step.)
        }, totalCalls * 120000);

        if (callback) callback(finalResponse);
      } catch (error) {
        if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') {
          toast.info(`Macro cancelado en paso ${stepNum}/${N}`);
        } else if (error.response?.status !== 401) {
          const motivo = error.response?.data?.detail || error.message || 'error desconocido';
          toast.error(`Macro detenido en paso ${stepNum}/${N}: ${motivo}`);
        }
        console.error('Error running macro:', error);
      } finally {
        setMacroProgress(null);
      }
    } else if (action === 'generateImage') {
      // Generate image from description
      try {
        const response = await axiosInstance.post(
          `/ai/generate-image`,
          {
            prompt: instruction,
            api_key: apiKey || undefined,
            // B5 fix (T-031): send the image model, not the chat model.
            model: imageModel
          }
        );

        const imageUrl = response.data.image_url;

        // Visibilidad §4: refresh badge + per-request charge toast.
        notifyCreditsCharged(response.data?.credits);

        if (callback) callback(imageUrl);
      } catch (error) {
        console.error('Error generating image:', error);
        if (error.response?.status !== 401 && !error.__creditsHandled) {
          toast.error(error.response?.data?.detail || 'Error al generar imagen');
        }
      }
    } else if (action === 'describeImage') {
      // Describe image from URL
      try {
        const response = await axiosInstance.post(
          `/ai/describe-image`,
          {
            image_url: originalText,
            prompt: instruction,
            api_key: apiKey || undefined,
            // T-036: send the vision/describe model, not the chat model.
            model: describeModel
          }
        );

        const description = response.data.description;

        // Visibilidad §4: refresh badge + per-request charge toast.
        notifyCreditsCharged(response.data?.credits);

        if (callback) callback(description);

        // T-022: mirror the description into the chat sidebar. NOTE: /ai/describe-image
        // does NOT persist to chat sessions (its backend is owned by another agent), so
        // this is a live, session-local append only — it does NOT survive reload.
        window.dispatchEvent(new CustomEvent('quickCommandChat', {
          detail: { instruction, response: description, sessionId: null }
        }));
      } catch (error) {
        console.error('Error describing image:', error);
        if (error.response?.status !== 401 && !error.__creditsHandled) {
          toast.error(error.response?.data?.detail || 'Error al describir imagen');
        }
      }
    } else {
      // Direct insert or replace action
      if (action === 'insert') {
        insertTextInEditor(instruction);
      } else if (action === 'replace') {
        replaceTextInEditor(instruction, originalText);
      }
    }
  };

  // Insert text: dispatch event with raw text to let the editor handle it.
  const insertTextInEditor = (text) => {
    window.dispatchEvent(new CustomEvent('insertText', {
      detail: {
        text: text, // Pass raw text
        savedSelection: savedSelectionRef.current
      }
    }));
  };

  const replaceTextInEditor = (newText, oldText) => {
    window.dispatchEvent(new CustomEvent('replaceText', {
      detail: {
        oldText,
        newText: newText, // Pass raw text
        savedSelection: savedSelectionRef.current
      }
    }));
  };

  // T-025: apply an accepted edit_sheet proposal to the LIVE editor (client-side;
  // the AI never wrote it server-side). If the user is on a different sheet, switch
  // to the proposal's sheet and ask them to re-apply (avoids a switch/apply race).
  const applyEditProposal = (proposal) => {
    if (!proposal) return 0;
    const editor = editorRef.current?.getEditor?.();
    if (!editor) {
      toast.error('El editor no está listo');
      return 0;
    }
    if (proposal.sheet_id && proposal.sheet_id !== activeSheetId) {
      setActiveSheetId(proposal.sheet_id);
      toast.info(`Cambié a la hoja "${proposal.sheet_name || ''}". Vuelve a pulsar Aplicar.`);
      return 0;
    }
    const count = findReplace(editor, {
      find: proposal.find,
      replace: proposal.replace,
      replaceAll: proposal.replace_all,
      occurrence: proposal.occurrence || 1,
    });
    if (count > 0) toast.success(`${count} reemplazo(s) aplicado(s)`);
    else toast.error('No se encontró el texto a reemplazar en esta hoja');
    return count;
  };

  // No longer needed - using continuous document mode only

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center app-gradient">
        <p className="text-slate-600 dark:text-slate-300">Cargando...</p>
      </div>
    );
  }

  // T-049: derive the active sheet + its orientation for the render (portrait
  // default). Deriving avoids a separate state that could drift from `sheets`.
  const activeSheet = sheets.find((s) => s.id === activeSheetId);
  const orientation = activeSheet?.orientation || 'portrait';
  const isSpreadsheetSheet = activeSheet?.content_type === 'spreadsheet';

  // T-120b: dropdown de Exportar reutilizado en desktop y en el slot compacto.
  const exportDropdown = (
    <UIDropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="export-button"
          variant="outline"
          disabled={exporting}
          className={`glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all flex items-center gap-2 ${exporting ? 'cursor-wait opacity-80' : ''}`}
          title="Exportar documento"
        >
          {exporting ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Download className="w-5 h-5 text-emerald-600" />
          )}
          <span className="text-xs font-medium">Exportar</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8}>
        <DropdownMenuItem onSelect={() => handleExport('docx')}>
          Word (.docx)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => handleExport('pdf')}>
          PDF (.pdf)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => handleExport('txt')}>
          Texto (.txt)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => handleExport('md')}>
          Markdown (.md)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => handleExportChanges()}>
          Registro de cambios IA (.csv)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => handleExportComments()}>
          Comentarios (.csv)
        </DropdownMenuItem>
        {isSpreadsheetSheet && (
          <>
            <DropdownMenuItem onSelect={() => handleExportSheet('csv')}>
              CSV (.csv)
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => handleExportSheet('xlsx')}>
              Excel (.xlsx)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </UIDropdownMenu>
  );

  // T-120b: header compacto — acciones modeladas como lista (sin duplicar JSX).
  // El slot rotativo muestra una accion a la vez; el menu de desborde lista todas.
  const headerActions = [
    { id: 'export', label: 'Exportar', icon: Download },
    { id: 'admin', label: 'Panel Admin', icon: Shield, onSelect: () => navigate('/admin', { state: { from: location.pathname + location.search } }), hidden: user?.role !== 'admin', testId: 'admin-button', iconClass: 'text-purple-600' },
    { id: 'config', label: 'Configuración', icon: Settings2, onSelect: () => setConfigOpen(true), testId: 'config-button' },
    { id: 'commands', label: 'Comandos rápidos IA', icon: Sparkles, onSelect: () => setCommandsOpen(true), testId: 'commands-button', iconClass: 'text-amber-500' },
    { id: 'flows', label: 'Flujos IA', icon: Workflow, onSelect: () => setFlowsOpen(true), testId: 'flows-button', iconClass: 'text-emerald-500' },
    { id: 'prompt', label: 'Prompt del documento', icon: FileText, onSelect: () => setPromptOpen(true), testId: 'prompt-button', iconClass: 'text-sky-600' },
    { id: 'diff', label: 'Comparar hojas', icon: GitCompare, onSelect: () => setDiffOpen(true), testId: 'diff-button', iconClass: 'text-teal-600' },
    { id: 'orientation', label: 'Orientación de la hoja', icon: orientation === 'landscape' ? RectangleHorizontal : RectangleVertical, onSelect: toggleOrientation, disabled: !activeSheetId, testId: 'orientation-toggle-button', iconClass: 'text-indigo-500' },
    { id: 'comments', label: 'Comentarios', icon: StickyNote, onSelect: () => setCommentsOpen((prev) => !prev), hidden: isSpreadsheetSheet, testId: 'comments-toggle-button', iconClass: 'text-amber-500' },
    { id: 'references', label: 'Referencias', icon: BookMarked, onSelect: () => window.dispatchEvent(new CustomEvent('openReferencesPanel')), testId: 'references-button', iconClass: 'text-sky-500' },
    { id: 'theme', label: 'Tema claro/oscuro', icon: Sun, onSelect: toggleTheme },
    { id: 'logout', label: 'Cerrar sesión', icon: LogOut, onSelect: () => setLogoutConfirmOpen(true), testId: 'editor-logout-button' },
  ];

  const headerSlotAction = headerActions.find((a) => a.id === headerSlot);

  return (
    <div className="h-screen flex app-gradient">
      <HelpPanel page="editor" />
      <div className="flex-1 min-w-0 h-screen flex flex-col">
      {/* Header */}
      <div className="glass border-b border-white/20 px-3 md:px-6 py-2 md:py-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex justify-between items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            <Button
              data-testid="back-button"
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="hover:bg-white/50 dark:hover:bg-slate-700/50 transition-all"
              title="Volver al dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg md:text-2xl font-bold truncate" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {document?.title}
            </h1>
          </div>
          {/* Orden del dueño (T-086): IA/trabajo diario → documento → estado y
              configuración → sesión. Separadores entre grupos; logout SIEMPRE
              al final y con confirmación. */}
          {isDesktop ? (
          <div className="flex gap-1 md:gap-2 items-center flex-wrap justify-end">
            {/* GRUPO A — admin · configuración · comandos IA */}
            {user?.role === 'admin' && (
              <Button
                data-testid="admin-button"
                variant="outline"
                onClick={() => navigate('/admin', { state: { from: location.pathname + location.search } })}
                className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
                title="Panel Admin"
              >
                <Shield className="w-5 h-5 text-purple-600" />
                <span className="hidden md:inline text-xs font-medium">Admin</span>
              </Button>
            )}
            <Button
              data-testid="config-button"
              variant="outline"
              onClick={() => setConfigOpen(true)}
              className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
              title="Configuración"
            >
              <Settings2 className="w-5 h-5" />
              <span className="hidden md:inline text-xs font-medium">Config</span>
            </Button>
            <Button
              data-testid="commands-button"
              variant="outline"
              onClick={() => setCommandsOpen(true)}
              className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
              title="Comandos rápidos IA"
            >
              <Sparkles className="w-5 h-5 text-amber-500" />
              <span className="hidden md:inline text-xs font-medium">Comandos</span>
            </Button>
            <Button data-testid="flows-button" variant="outline" onClick={() => setFlowsOpen(true)} className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all" title="Flujos IA — secuencias de pasos con modelo propio y asesores">
              <Workflow className="w-5 h-5 text-emerald-500" />
              <span className="hidden md:inline text-xs font-medium">Flujos</span>
            </Button>

            <div className="w-px h-6 bg-slate-300/60 dark:bg-slate-600/60 mx-1" />

            {/* GRUPO B — documento: exportar · prompt */}
            {exportDropdown}
            <Button
              data-testid="prompt-button"
              variant="outline"
              onClick={() => setPromptOpen(true)}
              className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all flex items-center gap-2"
              title="Instrucción del sistema para el documento"
            >
              <FileText className="w-5 h-5 text-sky-600" />
              <span className="text-xs font-medium">Prompt</span>
            </Button>
            {/* Comparar hojas vive en el grupo DOCUMENTO, no en Exportar: abre un
                diálogo de comparación, no descarga nada (orden del dueño). */}
            <Button
              data-testid="diff-button"
              variant="outline"
              onClick={() => setDiffOpen(true)}
              className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
              title="Comparar hojas"
            >
              <GitCompare className="w-5 h-5 text-teal-600" />
              <span className="hidden md:inline text-xs font-medium">Comparar</span>
            </Button>

            <div className="w-px h-6 bg-slate-300/60 dark:bg-slate-600/60 mx-1" />

            {/* GRUPO C — orientación · notas (amarillo) */}
            <Button
              data-testid="orientation-toggle-button"
              variant="outline"
              onClick={toggleOrientation}
              disabled={!activeSheetId}
              className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
              title={orientation === 'landscape' ? 'Orientación: horizontal (clic para vertical)' : 'Orientación: vertical (clic para horizontal)'}
            >
              {orientation === 'landscape'
                ? <RectangleHorizontal className="w-5 h-5 text-indigo-500" />
                : <RectangleVertical className="w-5 h-5 text-indigo-500" />}
              <span className="hidden md:inline text-xs font-medium">Orientar</span>
            </Button>
            {!isSpreadsheetSheet && (
              <Button
                data-testid="comments-toggle-button"
                onClick={() => {
                  // #4: la columna de comentarios es un overlay del área del
                  // editor (margen derecho), NO ocupa el carril lateral, así que
                  // convive con el chat/paneles sin cerrarlos.
                  setCommentsOpen((prev) => !prev);
                }}
                className={`transition-all ${commentsOpen ? 'bg-sky-500 text-white' : 'glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 text-slate-900 dark:text-slate-100'}`}
                title="Comentarios"
              >
                <StickyNote className="w-5 h-5 text-amber-500" />
                <span className="hidden md:inline text-xs font-medium">Comentarios</span>
              </Button>
            )}
            {/* T-246: botón de Referencias del documento */}
            {!isSpreadsheetSheet && (
              <Button
                data-testid="references-button"
                variant="outline"
                onClick={() => window.dispatchEvent(new CustomEvent('openReferencesPanel'))}
                className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
                title="Referencias del documento"
              >
                <BookMarked className="w-5 h-5 text-sky-500" />
                <span className="hidden md:inline text-xs font-medium">Referencias</span>
              </Button>
            )}

            <div className="w-px h-6 bg-slate-300/60 dark:bg-slate-600/60 mx-1" />

            {/* GRUPO D — créditos · tema ·(20px)· sesión */}
            {/* Visibilidad §4: saldo de créditos siempre visible + panel al hacer clic. */}
            <CreditBadge />
            <ThemeToggle className="glass border-0" />
            <div aria-hidden style={{ width: '20px' }} />
            {/* T-086 pedía el logout SIEMPRE al final; estaba antes del tema. El
                hueco de 20px lo separa para que no se pulse por error. */}
            <Button
              data-testid="editor-logout-button"
              variant="outline"
              onClick={() => setLogoutConfirmOpen(true)}
              className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
              title="Cerrar sesión"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden md:inline text-xs font-medium">Salir</span>
            </Button>
          </div>
          ) : (
          <div className="flex gap-1 items-center justify-end">
            {/* T-120b: header compacto bajo 1024px — CreditBadge + slot rotativo + menú de desborde */}
            <CreditBadge />
            {headerSlot === 'export' || !headerSlotAction ? (
              exportDropdown
            ) : (
              <Button
                data-testid={headerSlotAction.testId}
                variant="outline"
                className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
                title={headerSlotAction.label}
                onClick={headerSlotAction.onSelect}
              >
                <headerSlotAction.icon className={`w-5 h-5 ${headerSlotAction.iconClass || ''}`} />
              </Button>
            )}
            <UIDropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="header-overflow-button"
                  variant="outline"
                  className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
                  title="Mas opciones"
                >
                  <MoreHorizontal className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8}>
                {headerActions.filter((a) => !a.hidden).map((a) => (
                  <DropdownMenuItem
                    key={a.id}
                    disabled={a.disabled}
                    onSelect={() => {
                      if (a.id !== 'export') a.onSelect();
                      setHeaderSlot(a.id);
                    }}
                  >
                    <a.icon className="w-4 h-4" />
                    {a.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </UIDropdownMenu>
          </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex min-h-0 overflow-hidden relative">
        {/* Editor Area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
          {/* T-026: unmissable indicator while the AI works; editing is locked. */}
          {aiLocked && (
            <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-center gap-3 px-4 py-2 bg-sky-600 text-white text-sm shadow-md">
              <span className="animate-pulse">🤖 {macroProgress || 'IA trabajando en el documento…'}</span>
              <button
                type="button"
                onClick={cancelAiRequest}
                className="px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 transition-colors font-medium"
              >
                Cancelar
              </button>
            </div>
          )}
          {/* Sheet Tabs — locked while the AI works so switching/adding/deleting
              sheets can't race the in-flight write or misfile the chat reply.
              The "🤖 IA trabajando" banner already explains the wait. */}
          {sheets.length > 0 && (
            // T-122: bajo 1024px las pestañas no caben — solo el select.
            (!isDesktop || sheetsCollapsed) ? (
              <div className="px-3 md:px-6 py-1.5 border-b border-white/20 flex flex-wrap items-center gap-2">
                <select
                  value={activeSheetId || ''}
                  onChange={(e) => setActiveSheetId(e.target.value)}
                  disabled={aiLocked}
                  title="Elegir hoja"
                  className="text-sm rounded-md border border-white/20 bg-white/60 dark:bg-slate-800/60 px-2 py-1 max-w-[45%]"
                >
                  {groups.length === 0 ? (
                    sheets.map((s) => (
                      <option key={s.id} value={s.id}>{s.content_type === 'spreadsheet' ? '📊 ' : '📄 '}{s.is_source ? '🔒 ' : ''}{s.name}</option>
                    ))
                  ) : (
                    <>
                      {groups.map((g) => {
                        const groupSheets = sheets.filter((s) => s.group_id === g.id);
                        if (groupSheets.length === 0) return null;
                        return (
                          <optgroup key={g.id} label={g.name}>
                            {groupSheets.map((s) => (
                              <option key={s.id} value={s.id}>{s.content_type === 'spreadsheet' ? '📊 ' : '📄 '}{s.is_source ? '🔒 ' : ''}{s.name}</option>
                            ))}
                          </optgroup>
                        );
                      })}
                      {(() => {
                        const existingIds = new Set(groups.map((g) => g.id));
                        const orphans = sheets.filter((s) => !s.group_id || !existingIds.has(s.group_id));
                        if (orphans.length === 0) return null;
                        return (
                          <optgroup label="Sin grupo">
                            {orphans.map((s) => (
                              <option key={s.id} value={s.id}>{s.content_type === 'spreadsheet' ? '📊 ' : '📄 '}{s.is_source ? '🔒 ' : ''}{s.name}</option>
                            ))}
                          </optgroup>
                        );
                      })()}
                    </>
                  )}
                </select>
                <SheetTabs
                  controlsOnly
                  sheets={sheets}
                  activeSheetId={activeSheetId}
                  onSelectSheet={setActiveSheetId}
                  onAddSheet={createNewSheet}
                  onImportSheet={importSheet}
                  onDeleteSheet={deleteSheet}
                  onReorderSheets={reorderSheets}
                  onRenameSheet={renameSheet}
                  onToggleSource={toggleSource}
                  groups={groups}
                  onCreateGroup={createGroup}
                  onUpdateGroup={updateGroup}
                  onDeleteGroup={deleteGroup}
                  onAssignSheet={setSheetGroup}
                  onReorderRow={onReorderRow}
                />
                <button
                  type="button"
                  onClick={() => setReorderDialogOpen(true)}
                  title="Ordenar hojas"
                  className="ml-auto p-1 rounded hover:bg-white/50 dark:hover:bg-slate-700/50"
                >
                  <ArrowUpDown className="w-4 h-4" />
                </button>
                {isDesktop && (
                  <button
                    type="button"
                    onClick={toggleSheetsCollapsed}
                    title="Mostrar pestañas de hojas"
                    className="p-1 rounded hover:bg-white/50 dark:hover:bg-slate-700/50"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div
                className={
                  'px-6 py-3 border-b border-white/20 flex items-start gap-2' +
                  (aiLocked ? ' opacity-50 pointer-events-none' : '')
                }
                aria-disabled={aiLocked}
              >
                <div className="flex-1 min-w-0">
                  <SheetTabs
                    sheets={sheets}
                    activeSheetId={activeSheetId}
                    onSelectSheet={setActiveSheetId}
                    onAddSheet={createNewSheet}
                    onImportSheet={importSheet}
                    onDeleteSheet={deleteSheet}
                    onReorderSheets={reorderSheets}
                    onRenameSheet={renameSheet}
                    onToggleSource={toggleSource}
                    groups={groups}
                    onCreateGroup={createGroup}
                    onUpdateGroup={updateGroup}
                    onDeleteGroup={deleteGroup}
                    onAssignSheet={setSheetGroup}
                    onReorderRow={onReorderRow}
                  />
                </div>
                <button
                  type="button"
                  onClick={toggleSheetsCollapsed}
                  title="Ocultar pestañas de hojas (mas espacio)"
                  className="shrink-0 p-1 rounded hover:bg-white/50 dark:hover:bg-slate-700/50"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
              </div>
            )
          )}

          {/* Editor Content — T-049: switch by content_type. A spreadsheet sheet
              mounts SpreadsheetSheet (its own instance, keyed so it re-reads
              content on sheet change); a text sheet mounts ContinuousDocument.
              Distinct keys guarantee the two never share a stale instance. */}
          {sheets.length > 0 && activeSheetId && (
            isSpreadsheetSheet ? (
              <div className="flex-1 overflow-auto editor-canvas p-2 md:p-8">
                <div
                  className="editor-paper rounded-lg shadow-lg p-8 mx-auto bg-white dark:bg-slate-900"
                  style={{
                    minHeight: orientation === 'landscape' ? '816px' : '1056px',
                    width: orientation === 'landscape' ? '1056px' : '816px',
                    maxWidth: '100%',
                  }}
                >
                  <SpreadsheetSheet
                    key={`${activeSheetId}:${spreadsheetReloadNonce}`}
                    content={activeSheet?.content}
                    onUpdate={(c) => updateSheetContent(activeSheetId, c, 'spreadsheet')}
                    landscape={orientation === 'landscape'}
                  />
                </div>
              </div>
            ) : (
              <ContinuousDocument
                key={`doc-${activeSheetId}`}
                ref={editorRef}
                content={activeSheet?.content || ''}
                landscape={orientation === 'landscape'}
                chatOpen={chatOpen}
                onToggleChat={() => setChatOpen(!chatOpen)}
                zoom={zoom}
                onUpdate={(content) => updateSheetContent(activeSheetId, content)}
                onTextSelection={(text, position, type) => {
                  handleTextSelection(text, position, type);
                }}
                onTableOptionsClick={(table) => {
                  if (table) {
                    setSelectedTableForOptions(table);
                    setTableOptionsOpen(true);
                    // Close chat to allow table options panel to take its place
                    setChatOpen(false);
                    // Ensure properties panel is closed
                    setPropertiesOpen(false);
                  } else {
                    setSelectedTableForOptions(null);
                    setTableOptionsOpen(false);
                    // Restore chat sidebar when table options panel closes
                    setChatOpen(true);
                  }
                }}
              />
            )
          )}

          {/* T-032: word-count status bar at the foot of the editor column */}
          {sheets.length > 0 && (
            <WordCountBar
              sheets={sheets}
              activeSheetId={activeSheetId}
              zoom={isSpreadsheetSheet ? undefined : zoom}
              onZoomChange={isSpreadsheetSheet ? undefined : applyZoom}
            />
          )}
        </div>

        {/* #1: comments live in their OWN fixed-width flex column between the
            editor and the chat/properties rail, so all three share the space
            (no overlap, no cramming). */}
        <CommentsMarginColumn
          editor={editorRef.current?.getEditor?.()}
          scrollContainer={editorRef.current?.getScrollContainer?.()}
          sheetId={activeSheetId}
          open={commentsOpen}
          layoutSignal={`${chatOpen}|${propertiesOpen}|${tableOptionsOpen}|${isExpanded}`}
          onClose={() => setCommentsOpen(false)}
          runLocked={runLockedWithFlush}
          onDocumentModified={handleDocumentModified}
          getActiveSessionId={() => activeChatSessionRef.current}
          onProcessed={(sid) => {
            // #3: show the processing conversation in the chat.
            setChatOpen(true);
            window.dispatchEvent(new CustomEvent('commentsProcessed', { detail: { sessionId: sid } }));
          }}
          onInsertText={insertTextInEditor}
          onReplaceText={(newText) => replaceTextInEditor(newText, '')}
        />

        {/* Sidebar - Displays either Chat, Properties, or Table Options (mutually exclusive) */}
        {(chatOpen || propertiesOpen || tableOptionsOpen) && (
          <div
            className={`border-l border-white/20 glass slide-in flex-shrink-0 h-full transition-all duration-300 ${!isDesktop ? 'absolute inset-y-0 right-0 z-30 shadow-2xl' : 'relative'}`}
            style={{
              width: !isDesktop
                ? (tableOptionsOpen || propertiesOpen || isExpanded ? '85vw' : 'min(384px, 85vw)')
                : (tableOptionsOpen || propertiesOpen ? '384px' : (isExpanded ? '70vw' : '30vw'))
            }}
          >
            {tableOptionsOpen ? (
              <TableOptionsSidebar
                editor={editorRef.current?.getEditor?.()}
                selectedTable={selectedTableForOptions}
                onClose={() => {
                  setTableOptionsOpen(false);
                  setSelectedTableForOptions(null);
                  // Restore chat sidebar when closing table options
                  setChatOpen(true);
                }}
              />
            ) : propertiesOpen ? (
              <PropertiesPanel
                selectedNode={selectedImageNode}
                selectedNodePos={selectedImagePos}
                editor={editorRef.current?.getEditor?.()}
                onClose={closeProperties}
              />
            ) : chatOpen ? (
              <ChatSidebar
                documentId={documentId}
                sheets={sheets}
                activeSheetId={activeSheetId}
                onClose={() => setChatOpen(false)}
                onInsertText={insertTextInEditor}
                onReplaceText={(newText) => replaceTextInEditor(newText, '')}
                documentPrompt={document?.system_prompt}
                onContextChange={setChatContext}
                onToggleSize={toggleChatSize}
                isExpanded={isExpanded}
                onSessionChange={(id) => { activeChatSessionRef.current = id; }}
                onApplyProposal={applyEditProposal}
                runLocked={runLockedWithFlush}
                onDocumentModified={handleDocumentModified}
                onOpenCommandManager={(action) => {
                  setCommandManagerAction(action);
                  setCommandsOpen(true);
                }}
              />
            ) : null}
          </div>
        )}

      </div>

      <SheetDiffView
        open={diffOpen}
        onOpenChange={setDiffOpen}
        sheets={sheets}
        initialLeftId={activeSheetId}
      />

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="glass border-0 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configuración de IA</DialogTitle>
            <DialogDescription>
              Configura tu API key, modelo y prompt del sistema para la IA.
            </DialogDescription>
          </DialogHeader>
          <ConfigPanel />
        </DialogContent>
      </Dialog>

      {/* Commands Management Dialog */}
      <Dialog open={commandsOpen} onOpenChange={(open) => { setCommandsOpen(open); if (!open) setCommandManagerAction(null); }}>
        <DialogContent className="glass border-0 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Administrar Comandos</DialogTitle>
            <DialogDescription>
              Crea y gestiona comandos personalizados para automatizar tareas con IA.
            </DialogDescription>
          </DialogHeader>
          <CommandManagement
            onClose={() => { setCommandsOpen(false); setCommandManagerAction(null); }}
            initialAction={commandManagerAction}
          />
        </DialogContent>
      </Dialog>

      {/* El panel de administración vive ahora en la ruta /admin (no modal). */}

      {/* Document Prompt Dialog */}
      <Dialog open={promptOpen} onOpenChange={setPromptOpen}>
        <DialogContent className="glass border-0 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Prompt del Documento</DialogTitle>
            <DialogDescription>
              Define un prompt específico para este documento que se aplicará en todas las interacciones con IA.
            </DialogDescription>
          </DialogHeader>
          <DocumentPromptPanel
            documentId={documentId}
            currentPrompt={document?.system_prompt}
            onUpdate={(newPrompt) => {
              setDocument({ ...document, system_prompt: newPrompt });
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Floating AI Button */}
      <FloatingAIButton
        position={floatingButtonPos}
        onClick={openQuickCommandModal}
        portalContainer={editorRef.current?.getScrollContainer?.()}
      />

      {/* Image properties button — floats 10px under the AI button when an image
          is selected (replaces the old top-toolbar + on-image buttons). */}
      <FloatingImagePropsButton
        position={floatingButtonPos}
        show={hasImageSelected}
        onClick={() => window.dispatchEvent(new CustomEvent('openImageProperties', { detail: { pos: selectedImagePos } }))}
        portalContainer={editorRef.current?.getScrollContainer?.()}
      />

      {/* Comment button — floats 10px ABOVE the AI button on ANY selection (text
          OR image); opens the comments column and starts composing. For an image
          it stacks above the AI button while the properties button sits below. */}
      <FloatingCommentButton
        position={floatingButtonPos}
        show
        onClick={() => {
          setCommentsOpen(true);
          window.dispatchEvent(new CustomEvent('startCommentCompose'));
        }}
        portalContainer={editorRef.current?.getScrollContainer?.()}
      />

      {/* Quick Command Modal */}
      {quickCommandModal && (
        <QuickCommandModal
          selectedContent={quickCommandModal.content}
          position={quickCommandModal.position}
          onClose={(payload) => {
            const reason = typeof payload === 'string' ? payload : (payload?.reason || 'unknown');
            setQuickCommandModal(null);

            if (reason === 'backdrop') {
              // User clicked outside: treat as leaving selection context
              setFloatingButtonPos(null);
              setSelectedTextForAI({ text: '', images: [], html: '', hasTables: false });
              savedSelectionRef.current = null;

              // Collapse selection in editor
              const editor = editorRef.current?.getEditor?.();
              if (editor) {
                try {
                  const pos = editor.state.selection.to;
                  editor.chain().setTextSelection(pos).run();
                } catch {}
              }
              return;
            }

            // For other close reasons (ESC, button), restore only if selection still exists
            setTimeout(() => {
              const editor = editorRef.current?.getEditor?.();
              if (editor) {
                const sel = editor.state.selection;
                const text = editor.state.doc.textBetween(sel.from, sel.to, ' ');
                // A selected image is a NodeSelection (sel.node): textBetween is
                // empty for it, so restore on a live node selection too — else the
                // button vanishes until the image is re-selected.
                const stillSelected = text.trim().length > 0 || sel.node != null;
                if (stillSelected) {
                  // T-053: la selección sigue viva → restaura el botón flotante
                  // (permanece visible hasta que el usuario deseleccione).
                  if (lastFloatingButtonPosRef.current) {
                    setFloatingButtonPos({ ...lastFloatingButtonPosRef.current, visible: true });
                  }
                } else {
                  setFloatingButtonPos(null);
                }
              }
            }, 50);
          }}
          onAction={handleQuickAction}
        />
      )}

      {flowsOpen && (
        <FlowsModal
          open={flowsOpen}
          onClose={() => setFlowsOpen(false)}
          onRun={(flow) => {
            setFlowsOpen(false);
            // T-234: reusar el orquestador de macros (T-231) — un flujo tiene el
            // mismo shape que un comando macro. Texto base = selección actual si la hay.
            const baseText = (typeof selectedTextForAI === 'string' ? selectedTextForAI : selectedTextForAI?.text) || '';
            handleQuickAction('macro', flow, baseText, () => {
              toast.success(`Flujo "${flow.title}" completado`);
            });
          }}
        />
      )}

      {/* T-054: lightbox singleton — abierto por doble-click en imágenes (evento openImageLightbox) */}
      <ImageLightboxHost />
      {/* T-246: host del panel de Referencias del documento */}
      <ReferencesHost documentId={documentId} sheets={sheets} onGoToSource={handleGoToSource} />

      {/* T-086: cerrar sesión pide confirmación (el trabajo ya está autosalvado). */}
      <ConfirmDialog
        open={logoutConfirmOpen}
        onOpenChange={setLogoutConfirmOpen}
        title="¿Cerrar sesión?"
        description="Tu trabajo se guarda automáticamente. Tendrás que iniciar sesión de nuevo para continuar."
        confirmText="Cerrar sesión"
        cancelText="Cancelar"
        onConfirm={() => { setLogoutConfirmOpen(false); logout(); }}
      />

      {/* T-128: reordenar hojas en móvil/tablet (el <select> solo elige). */}
      <SheetReorderDialog
        open={reorderDialogOpen}
        onOpenChange={setReorderDialogOpen}
        sheets={sheets}
        groups={groups}
        onReorder={reorderSheets}
      />

      </div>
    </div>
  );
};

export default EditorPage;
